import React, { useState } from 'react';
import {
  Button, ThemeProvider, CssBaseline, TextField, Box, Typography, Container, CircularProgress, Alert, Link, Paper, Snackbar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { createTheme } from '@mui/material/styles';
import Footer from '../pages/Footer'; // Ensure correct path to the Footer component
import CommonAppBar from './CommonAppBar';
import './styles.css'; // Import the CSS file

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: 'rgb(18,82,166)',
    },
    background: {
      default: '#f8fafc',
    },
  },
  typography: {
    fontFamily: '"Times New Roman", Times, serif',
  },
});

const ForgetPassword = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ open: false, message: '', severity: 'info' });
  const [isDisabled, setIsDisabled] = useState(false); // New state to control disabling of fields
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setEmailError('');
    setIsDisabled(true); // Disable fields on submission

    try {
      const response = await axios.post('https://ngat.ethernet.edu.et/api/password-reset-request', { email });

      if (response.status === 200) {
        setAlertMessage({ open: true, message: 'Password reset link has been successfully sent to your email. Please follow the instructions there.', severity: 'success' });
      } else if (response.status === 404) {
        setEmailError('The provided email address was not found.');
      } else {
        setAlertMessage({ open: true, message: 'Failed to send password reset link. Please try again later.', severity: 'error' });
      }
    } catch (error) {
      console.error('Password reset error:', error.response || error);
      if (error.response && error.response.data && error.response.data.errors && error.response.data.errors.email) {
        setEmailError('The provided email address was not found. Make sure you\'ve registered or activated your account.');
      } else {
        setAlertMessage({ open: true, message: 'Failed to send password reset link. Please try again later.', severity: 'error' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setAlertMessage({ ...alertMessage, open: false });
    if (alertMessage.severity === 'success') {
      navigate('/login'); // Redirect to login if successful
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <div className="page-container">
        {/* Global Mobile TextField Fixes */}
        <style>
          {`
            @media (max-width: 600px) {
              .MuiTextField-root .MuiOutlinedInput-root {
                min-height: 48px !important;
              }
              .MuiTextField-root .MuiInputLabel-root {
                line-height: 1.2 !important;
              }
              .MuiTextField-root .MuiOutlinedInput-notchedOutline {
                border-radius: 8px !important;
              }
              .MuiTextField-root .MuiOutlinedInput-input {
                padding: 12px 14px !important;
              }
            }
          `}
        </style>
        <CssBaseline />
        
        {/* Background Effect */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                radial-gradient(circle at 20% 80%, rgba(18,82,166,0.08) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(18,82,166,0.08) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(18,82,166,0.06) 0%, transparent 50%)
              `,
              pointerEvents: 'none'
            }
          }}
        />
        
        {/* Connected Particle Network Effect */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(2px 2px at 20px 30px, rgba(18,82,166,0.15), transparent),
              radial-gradient(2px 2px at 40px 70px, rgba(18,82,166,0.12), transparent),
              radial-gradient(1px 1px at 90px 40px, rgba(18,82,166,0.1), transparent),
              radial-gradient(1px 1px at 130px 80px, rgba(18,82,166,0.08), transparent),
              radial-gradient(2px 2px at 160px 30px, rgba(18,82,166,0.15), transparent),
              radial-gradient(2px 2px at 200px 90px, rgba(18,82,166,0.12), transparent),
              radial-gradient(1px 1px at 240px 50px, rgba(18,82,166,0.1), transparent),
              radial-gradient(1px 1px at 280px 120px, rgba(18,82,166,0.08), transparent),
              radial-gradient(2px 2px at 320px 40px, rgba(18,82,166,0.15), transparent),
              radial-gradient(2px 2px at 360px 100px, rgba(18,82,166,0.12), transparent),
              radial-gradient(1px 1px at 440px 140px, rgba(18,82,166,0.08), transparent),
              radial-gradient(2px 2px at 480px 30px, rgba(18,82,166,0.15), transparent),
              radial-gradient(2px 2px at 520px 80px, rgba(18,82,166,0.12), transparent),
              radial-gradient(1px 1px at 560px 50px, rgba(18,82,166,0.1), transparent),
              radial-gradient(1px 1px at 600px 110px, rgba(18,82,166,0.08), transparent),
              radial-gradient(2px 2px at 640px 70px, rgba(18,82,166,0.15), transparent),
              radial-gradient(2px 2px at 680px 130px, rgba(18,82,166,0.12), transparent),
              radial-gradient(1px 1px at 720px 40px, rgba(18,82,166,0.1), transparent),
              radial-gradient(1px 1px at 760px 90px, rgba(18,82,166,0.08), transparent),
              radial-gradient(2px 2px at 800px 60px, rgba(18,82,166,0.15), transparent),
              radial-gradient(2px 2px at 840px 120px, rgba(18,82,166,0.12), transparent),
              radial-gradient(1px 1px at 880px 80px, rgba(18,82,166,0.1), transparent),
              radial-gradient(1px 1px at 920px 140px, rgba(18,82,166,0.08), transparent),
              radial-gradient(2px 2px at 960px 50px, rgba(18,82,166,0.15), transparent),
              radial-gradient(2px 2px at 1000px 100px, rgba(18,82,166,0.12), transparent),
              radial-gradient(1px 1px at 1040px 70px, rgba(18,82,166,0.1), transparent),
              radial-gradient(1px 1px at 1080px 130px, rgba(18,82,166,0.08), transparent),
              radial-gradient(2px 2px at 1120px 40px, rgba(18,82,166,0.15), transparent),
              radial-gradient(2px 2px at 1160px 90px, rgba(18,82,166,0.12), transparent),
              radial-gradient(1px 1px at 1200px 60px, rgba(18,82,166,0.1), transparent),
              radial-gradient(1px 1px at 1240px 110px, rgba(18,82,166,0.08), transparent)
            `,
            backgroundSize: '1280px 160px',
            backgroundRepeat: 'repeat',
            pointerEvents: 'none',
            zIndex: 0
          }}
        />
        
        {/* Molecular Structure */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(4px 4px at 80px 80px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 240px 240px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 400px 400px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 560px 560px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 720px 720px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 880px 880px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 1040px 1040px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 1200px 1200px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 1360px 1360px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 1520px 1520px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 1680px 1680px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 1840px 1840px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 2000px 2000px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 2160px 2160px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 2320px 2320px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 2480px 2480px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 2640px 2640px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 2800px 2800px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 2960px 2960px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 3120px 3120px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 3280px 3280px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 3440px 3440px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 3600px 3600px, rgba(18,82,166,0.2), transparent),
              radial-gradient(4px 4px at 3760px 3760px, rgba(18,82,166,0.2), transparent)
            `,
            backgroundSize: '3840px 3840px',
            backgroundRepeat: 'repeat',
            pointerEvents: 'none',
            zIndex: 0
          }}
        />
        
        {/* Grid Structure */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(rgba(18,82,166,0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(18,82,166,0.04) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            pointerEvents: 'none',
            zIndex: 0
          }}
        />

        {/* Main Content */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <CommonAppBar 
            title="Ministry of Education"
            showHomeButton={true}
            showContactButton={true}
            showLoginButton={true}
            customButtons={[
              {
                label: 'Register',
                onClick: () => navigate('/registration')
              }
            ]}
          />

          <Box sx={{ 
            minHeight: 'calc(100vh - 140px)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 4
          }}>
            <Container component="main" sx={{ 
              maxWidth: 'sm',
              px: { xs: 2, md: 3 }
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                width: '100%'
              }}>
                
                {/* Header Section */}
                <Box sx={{ 
                  textAlign: 'center', 
                  mb: 4,
                  width: '100%'
                }}>
                  <Typography variant="h5" sx={{ 
                    mb: 1, 
                    color: '#000000', 
                    fontWeight: 700,
                    letterSpacing: '-0.5px'
                  }}>
                    National Graduate Admission Test (NGAT)
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: '#64748b', 
                    lineHeight: 1.6,
                    maxWidth: 480,
                    mx: 'auto'
                  }}>
                    Reset your password to regain access to your account and continue with your application process.
            </Typography>
                </Box>

                {/* Main Form Card */}
                <Paper elevation={0} sx={{ 
                  width: '100%',
                  maxWidth: 480,
                  p: { xs: 2, md: 4 },
                  backgroundColor: '#ffffff',
                  borderRadius: { xs: 2, md: 3 },
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Decorative accent */}
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'linear-gradient(90deg, rgb(18,82,166) 0%, rgb(59,130,246) 100%)'
                  }} />
                  
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="h5" sx={{ 
                      mb: 3, 
                      color: '#1e293b',
                      fontWeight: 600,
                      textAlign: 'center'
                    }}>
                      Forgot Password
            </Typography>

                    <Box component="form" onSubmit={handleSubmit}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!emailError}
                helperText={emailError}
                        disabled={isDisabled}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'rgb(18,82,166)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'rgb(18,82,166)',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.875rem', md: '1rem' },
                            '&.Mui-focused': {
                              color: 'rgb(18,82,166)',
                            },
                          },
                          '& .MuiOutlinedInput-input': {
                            fontSize: { xs: '0.875rem', md: '1rem' },
                            padding: { xs: '12px 14px', md: '16px 14px' },
                          },
                          '& .MuiFormHelperText-root': {
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                          },
                          // Mobile-specific fixes
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderWidth: { xs: '1px', md: '1px' },
                          },
                          '& .MuiInputLabel-shrink': {
                            transform: { xs: 'translate(14px, -9px) scale(0.75)', md: 'translate(14px, -9px) scale(0.75)' },
                          },
                        }}
                      />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                        disabled={isSubmitting || isDisabled}
                        sx={{
                          mt: 3,
                          mb: 2,
                          py: 1.5,
                          backgroundColor: 'rgb(18,82,166)',
                          '&:hover': {
                            backgroundColor: 'rgb(15,70,140)',
                          },
                          fontWeight: 600,
                          fontSize: '1rem',
                          textTransform: 'none',
                          borderRadius: 2,
                        }}
                      >
                        {isSubmitting ? (
                          <CircularProgress size={24} sx={{ color: 'white' }} />
                        ) : (
                          'Send Reset Link'
                        )}
              </Button>

                      <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => navigate('/login')}
                          sx={{
                            color: 'rgb(18,82,166)',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          Back to Login
                </Link>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Container>
          </Box>
        </Box>
        
        {/* Success/Error Snackbar */}
        <Snackbar
          open={alertMessage.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={alertMessage.severity}
            sx={{ width: '100%' }}
          >
            {alertMessage.message}
          </Alert>
        </Snackbar>
        
        <Footer />
      </div>
    </ThemeProvider>
  );
};

export default ForgetPassword;
